
import IconButton from 'components/atoms/button/IconButton';
import Form from 'components/atoms/form/Form';
import withModal from 'lib/helpers/hoc/withModal'

import React, { FC, useState } from 'react'
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Icon from 'components/atoms/icon/Icon';
import { IconType } from 'lib/helpers/constants/iconTypes';
import RangeSlider from 'components/atoms/range-slider/RangeSlider';
import { useEditCommission } from 'lib/hooks/mutations/Finances/useEditCommission';
import DeleteModal from 'components/molecules/delete-modal/DeleteModal';
import { useDeleteCommission } from 'lib/hooks/mutations/Finances/useDeleteCommission';
import { usePermissions } from 'lib/hooks/shared/usePermissions';
import ConditionalRender from 'components/atoms/conditional-render/ConditionalRender';
import Input from 'components/atoms/input/Input';
import RadioButton from 'components/atoms/radio-button/RadioButton';
import Label from 'components/atoms/input/input-label/InputLabel';
import CurrencyInput from 'components/atoms/input/currency-input/CurrencyInput';

export interface IManageCommissionModuleProps {
    onConfirm: () => void;
    data: any,
    setIsAddCommissionCompanyOpen: (value: { id: string, name: string }) => void
    setIsAddCommissionOpen: (id: string) => void
    currentStep: number
    setCurrentStep: (value: number) => void
}
const ManageCommissionModule: FC<IManageCommissionModuleProps> = ({
    onConfirm,
    data,
    setIsAddCommissionCompanyOpen,
    setIsAddCommissionOpen,
    currentStep,
    setCurrentStep
}) => {

    const { t } = useTranslation();

    const hasPermissionToCreateCommission = usePermissions(['commission_models_create']);
    const hasPermissionToEditCommission = usePermissions(['commission_models_edit']);
    const hasPermissionToDeleteCommission = usePermissions(['commission_models_delete']);

    // Map commissions into an object keyed by `id`
    function mapCommissions() {
        const commissions: Record<number, any> = {};

        data.companies.forEach((company: any) => {
            company.companies_commissions.forEach((commission: any) => {
                commissions[commission.id] = {
                    company_commission: commission.company_commission,
                    company_commission_on_recomandation_cases: commission.company_commission_on_recomandation_cases
                };
            });
        });

        return commissions;
    }

    function mapStornoDeposite() {
        const stornodeposits: Record<number, any> = {};

        data.companies.forEach((company: any) => {
            company.companies_commissions.forEach((commission: any) => {
                stornodeposits[commission.id] = {
                    company_storno_deposit: commission.company_storno_deposit
                };
            });
        });

        return stornodeposits;
    }

    function mapExpectedMultiplier() {
        const multiplier: Record<number, any> = {};

        data.companies.forEach((company: any) => {
            company.companies_commissions.forEach((commission: any) => {
                multiplier[commission.id] = {
                    multiply_expected_commission: commission.multiply_expected_commission
                };
            });
        });

        return multiplier;
    }

    function mapExpectedCommissionMeasurementType() {
        const multiplier: Record<number, any> = {};

        data.companies.forEach((company: any) => {
            company.companies_commissions.forEach((commission: any) => {
                multiplier[commission.id] = {
                    expected_commission_measurement_type: commission.expected_commission_measurement_type
                };
            });
        });


        return multiplier;
    }

    const {
        control,
        handleSubmit,
        watch,
    } = useForm({
        defaultValues: {
            commission_id: data.id,
            commissions: mapCommissions(), // Use the keyed object here
            company_storno_deposit: mapStornoDeposite(),
            multiply_expected_commission: mapExpectedMultiplier(),
            expected_commission_measurement_type: mapExpectedCommissionMeasurementType()
        }
    });

    const { mutateAsync, isLoading } = useEditCommission()

    const onSubmit = (formData: any) => {
      
        const allKeys = new Set([
            ...Object.keys(formData.commissions || {}),
            ...Object.keys(formData.company_storno_deposit || {}),
            ...Object.keys(formData.multiply_expected_commission || {}),
            ...Object.keys(formData.expected_commission_measurement_type || {})
        ]);
        const finalData = {
            commission_id: formData.commission_id,
            commissions: Array.from(allKeys).map((key) => ({
                finance_company_commission_id: key,
                company_commission: formData.commissions?.[key]?.company_commission ?? null,
                company_commission_on_recomandation_cases: formData.commissions?.[key]?.company_commission_on_recomandation_cases ?? null,
                company_storno_deposit: formData.company_storno_deposit?.[key]?.company_storno_deposit ?? null,
                multiply_expected_commission: formData.multiply_expected_commission?.[key]?.multiply_expected_commission ?? null,
                expected_commission_measurement_type: formData.expected_commission_measurement_type?.[key]?.expected_commission_measurement_type ?? null
            }))
        }
        

        mutateAsync(finalData).then(() => {
            onConfirm()
        })
    };

    const { mutateAsync: DeleteCommision, isLoading: isLoadingDelete } = useDeleteCommission()

    const [DeleteData, setDeleteData] = useState({ id: '', name: '' })

    return (
        <div className='relative'>
            {currentStep === 1 ? (
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-0 flex flex-col gap-5">
                        <div className="flex flex-col gap-5">
                            {data.companies.map((company: any) => (
                                <div key={company.id}>
                                    <div className="text-xs text-primaryTextColor font-inter-semibold pb-3">
                                        {company.name}
                                    </div>
                                    <div className="flex gap-3 flex-wrap">
                                        {company.companies_commissions.map((commission: any) => (
                                            <div
                                                key={commission.id}
                                                className="border border-[#F2F4F7] px-5 py-3 rounded-[8px] w-full md:max-w-[45%] lg:max-w-[345px] flex flex-col gap-5"
                                            >
                                                <div className="flex gap-3 w-full items-center">
                                                    {commission.partner.logo !== null ? (
                                                        <img alt="" className='h-[36px] w-[36px] object-contain rounded-full'
                                                            src={'https://mainhub.insuhub.ch/api/partners/logo/' + commission.partner.logo}
                                                        />
                                                    ) : (
                                                        <img alt="" className='h-[36px] w-[36px] object-contain rounded-full'
                                                            src="https://st3.depositphotos.com/9998432/13335/v/450/depositphotos_133351928-stock-illustration-default-placeholder-man-and-woman.jpg"
                                                        />
                                                    )}
                                                    <span className="text-sm text-primaryTextColor font-inter-regular">
                                                        {commission.partner.name} {commission.partner_product != null ? ' - (' + commission.partner_product.products_name + ')' : ''}
                                                    </span>
                                                    <ConditionalRender condition={hasPermissionToDeleteCommission}>
                                                        <div className="ml-auto">
                                                            <div className='cursor-pointer' onClick={() => {
                                                                setCurrentStep(2)
                                                                setDeleteData({ id: commission.id, name: commission.partner.name + ' ' + company.name })
                                                            }}>
                                                                <Icon iconType={IconType.DELETE_CAMPAIGN_FIELDS_ICON} />
                                                            </div>
                                                        </div>
                                                    </ConditionalRender>
                                                </div>
                                                <Controller
                                                    name={`commissions[${commission.id}].company_commission` as any} // Use `commission.id` as key
                                                    control={control}
                                                    defaultValue={
                                                        (control as any)._defaultValues.commissions[commission.id]
                                                            ?.company_commission || 0
                                                    } // Fallback to 0 if value is missing
                                                    render={({ field }) => (
                                                        <RangeSlider
                                                            value={field.value} // Bind value from react-hook-form
                                                            onChange={field.onChange} // Update value in react-hook-form
                                                            rightLabel="Worker"
                                                            leftLabel="Company Profit"
                                                        />
                                                    )}
                                                />

                                                <div className='border border-border p-2 rounded-lg'>
                                                    <Label>{t('Commisions on Recomandation Cases')}</Label>
                                                    <Controller
                                                        name={`commissions[${commission.id}].company_commission_on_recomandation_cases` as any} // Use `commission.id` as key
                                                        control={control}
                                                        defaultValue={
                                                            (control as any)._defaultValues.commissions[commission.id]
                                                                ?.company_commission_on_recomandation_cases || 0
                                                        } // Fallback to 0 if value is missing
                                                        render={({ field }) => (
                                                            <RangeSlider
                                                                value={field.value} // Bind value from react-hook-form
                                                                onChange={field.onChange} // Update value in react-hook-form
                                                                rightLabel="Worker"
                                                                leftLabel="Company Profit"
                                                            />
                                                        )}
                                                    />
                                                </div>

                                                <Controller
                                                    name={`company_storno_deposit[${commission.id}].company_storno_deposit` as any} // Use `commission.id` as key
                                                    control={control}

                                                    render={({ field }) => (
                                                        <RangeSlider
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            rightLabel=' '
                                                            leftLabel='Storno Deposit'
                                                        />
                                                    )}
                                                />

                                                <div className='grid grid-cols-4 gap-3 items-end border border-border p-2 rounded-lg'>
                                                    <div className="col-span-4">
                                                        <Label>{t('Expected Commission Measurement Type')}</Label>
                                                    </div>
                                                    <div className='col-span-2'>
                                                        <Controller
                                                            name={`expected_commission_measurement_type[${commission.id}].expected_commission_measurement_type` as any}
                                                            control={control}

                                                            render={({ field }) => (
                                                                <RadioButton
                                                                    label={t('Multiplier')}
                                                                    isChecked={field.value === 'multiply'}
                                                                    value={'multiply'}
                                                                    onChange={() => {
                                                                        field.onChange('multiply');
                                                                    }}
                                                                    name={`expected_commission_measurement_type[${commission.id}].expected_commission_measurement_type`}
                                                                    labelClassName="h-[41px] 2xl:h-[46px] items-center flex bg-white"
                                                                    iconWrapperClassName="!top-[20px] 2xl:!top-[22px] !right-[6px]"
                                                                    className="right-[8px]"
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    <div className='col-span-2'>
                                                        <Controller
                                                            name={`expected_commission_measurement_type[${commission.id}].expected_commission_measurement_type` as any}
                                                            control={control}

                                                            render={({ field }) => (
                                                                <RadioButton
                                                                    label={t('Fixed Amount')}
                                                                    isChecked={field.value === 'fixed_amount'}
                                                                    value={'fixed_amount'}
                                                                    onChange={() => {
                                                                        field.onChange('fixed_amount');
                                                                    }}
                                                                    name={`expected_commission_measurement_type[${commission.id}].expected_commission_measurement_type`}

                                                                    labelClassName="h-[41px] 2xl:h-[46px] items-center flex bg-white"
                                                                    iconWrapperClassName="!top-[20px] 2xl:!top-[22px] !right-[6px]"
                                                                    className="right-[8px]"
                                                                />
                                                            )}
                                                        />
                                                    </div>

                                                    {/* <Controller
                                                        name={`commissions[${commission.id}].multiply_expected_commission` as any}
                                                        control={control}
                                                        defaultValue={
                                                            (control as any)._defaultValues.multiply_expected_commission[commission.id]
                                                                ?.multiply_expected_commission || 0
                                                        }
                                                        render={({ field }) => (
                                                            <Input
                                                                label={t('Expected Commission')}
                                                                placeholder={watch(`commissions[${commission.id}].expected_commission_measurement_type` as any) === 'multiply' ? '2x' : t("100 CHF")}
                                                                type="number"
                                                                className="w-full"
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                            />
                                                        )}
                                                    /> */}
                                                    <div className='col-span-4'>
                                                        {watch(`expected_commission_measurement_type[${commission.id}].expected_commission_measurement_type` as any) === 'multiply' ? (
                                                            <Controller
                                                                name={`multiply_expected_commission[${commission.id}].multiply_expected_commission` as any}
                                                                control={control}

                                                                render={({ field }) => (
                                                                    <Input
                                                                        placeholder={'2x'}
                                                                        type="number"
                                                                        className="w-full"
                                                                        value={field.value}
                                                                        onChange={field.onChange}
                                                                    />
                                                                )}
                                                            />
                                                        ) : (
                                                            <Controller
                                                                name={`multiply_expected_commission[${commission.id}].multiply_expected_commission` as any}
                                                                control={control}

                                                                render={({ field }) => {

                                                                    return (
                                                                        <CurrencyInput
                                                                            placeholder={t("100 CHF")}
                                                                            prefix="CHF "
                                                                            value={field?.value as any || ""}
                                                                            onValueChange={(value: any) => {
                                                                                field.onChange(value || "");
                                                                            }}

                                                                        />
                                                                    )
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>

                                            </div>
                                        ))}
                                        <ConditionalRender condition={hasPermissionToCreateCommission}>
                                            <div
                                                className="w-[44px] rounded-[8px] h-[88px] border border-dashed border-[#F2F4F7] flex items-center justify-center cursor-pointer"
                                                onClick={() => {
                                                    onConfirm();
                                                    setIsAddCommissionCompanyOpen({ name: company.name, id: company.id })
                                                }}
                                            >
                                                <Icon iconType={IconType.PLUS_ICON} color="#7D8592" />
                                            </div>
                                        </ConditionalRender>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-row justify-end gap-5 pt-10">
                        <ConditionalRender condition={hasPermissionToCreateCommission}>
                            <div className="flex gap-2 items-center cursor-pointer" onClick={() => {
                                onConfirm();
                                setIsAddCommissionOpen(data.id)
                            }}>
                                <Icon iconType={IconType.PLUS_ICON} color="var(--theme)" />
                                <span className="font-inter-semibold text-[--theme] cursor-pointer"  >
                                    {t("Add new insurance company")}
                                </span>
                            </div>
                        </ConditionalRender>
                        <ConditionalRender condition={hasPermissionToEditCommission}>
                            <IconButton className="w-full max-w-[150px]" type="submit" disabled={isLoading}>
                                {t('Update')}
                            </IconButton>
                        </ConditionalRender>
                    </div>


                </Form>
            ) : (
                <DeleteModal
                    onConfirm={onConfirm}
                    data={DeleteData}
                    deleteFunction={DeleteCommision}
                    isLoading={isLoadingDelete}
                />
            )}


        </div>

    );
};

export default withModal(ManageCommissionModule);


